import { useEffect, useState } from "react";
import { Menu, X, Languages, Flame, Timer } from "lucide-react";
import { useLang, tr } from "@/i18n";

const links = [
    { label: "Home", href: "#top" },
    { label: "Packages", href: "#packages" },
    { label: "Reviews", href: "#reviews" },
    { label: "Accommodation", href: "#accommodation" },
    { label: "Payment", href: "#payment" },
    { label: "Blog", href: "#blog" },
    { label: "About", href: "#about" },
];

const PITRU_START = new Date("2026-09-26T00:00:00+05:30").getTime();
const PITRU_END = new Date("2026-10-11T00:00:00+05:30").getTime();

const usePitruCountdown = (lang) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    if (now >= PITRU_END) return null;
    if (now >= PITRU_START) {
        return tr(lang, "Pitrupaksha is live — book your tithi now");
    }
    const diff = Math.floor((PITRU_START - now) / 1000);
    const d = Math.floor(diff / 86400);
    const h = Math.floor((diff % 86400) / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    const time =
        lang === "hi"
            ? `${d} दिन ${h} घंटे ${m} मिनट ${s} सेकंड`
            : `${d}d ${h}h ${m}m ${s}s`;
    return `${tr(lang, "starts in")} ${time}`;
};

export const Navbar = ({ settings }) => {
    const [open, setOpen] = useState(false);
    const [banner, setBanner] = useState(() => sessionStorage.getItem("ds_banner_closed") !== "1");
    const { lang, setLang } = useLang();
    const countdown = usePitruCountdown(lang);
    const t = (s) => tr(lang, s);
    const logo = settings?.logo_data || "/logo.jpg";
    const langLabel = lang === "en" ? "हिंदी" : "English";
    return (
        <header
            data-testid="main-navbar"
            className="fixed top-0 inset-x-0 z-50 bg-[#FAF6F0]/95 backdrop-blur-md border-b border-[#E6DBCF]"
        >
            {banner && countdown && (
                <div data-testid="pitrupaksha-banner" className="bg-gradient-to-r from-[#9C370B] via-[#C85A17] to-[#9C370B] text-white">
                    <div className="max-w-7xl mx-auto px-4 md:px-12 py-2 flex items-center justify-center gap-3 flex-wrap">
                        <Flame size={14} className="text-amber-200 shrink-0 hidden sm:block" />
                        <p className="text-[11px] sm:text-xs font-medium tracking-wide text-center">
                            <span className="text-amber-200 font-semibold">{t("Pitrupaksha 2026: 26 Sep – 10 Oct")}</span>
                            <span className="hidden md:inline"> · {t("Sarva Pitru Amavasya on 10 Oct — reserve your Shradh date early, limited panda availability")}</span>
                        </p>
                        <span
                            data-testid="pitrupaksha-countdown"
                            className="shrink-0 inline-flex items-center gap-1.5 bg-black/30 rounded-full px-3 py-1 text-[11px] sm:text-xs font-semibold text-amber-200 tabular-nums"
                        >
                            <Timer size={12} className="animate-pulse" /> {countdown}
                        </span>
                        <a
                            href="#book"
                            data-testid="pitrupaksha-banner-cta"
                            className="shrink-0 text-[11px] sm:text-xs font-semibold underline underline-offset-2 hover:text-amber-200 transition-colors"
                        >
                            {t("Reserve Your Date")}
                        </a>
                        <button
                            data-testid="pitrupaksha-banner-close"
                            onClick={() => { setBanner(false); sessionStorage.setItem("ds_banner_closed", "1"); }}
                            aria-label="Close banner"
                            className="shrink-0 text-white/70 hover:text-white transition-colors"
                        >
                            <X size={14} />
                        </button>
                    </div>
                </div>
            )}
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
                <a href="#top" data-testid="nav-brand-logo" className="flex items-center gap-3">
                    <img
                        src={logo}
                        alt="Dharm Sarovar logo"
                        data-testid="nav-logo-image"
                        className="h-11 w-11 rounded-xl object-cover border border-[#E5C158] shadow-sm"
                    />
                    <span className="leading-tight">
                        <span className="block font-display font-semibold text-lg tracking-wide text-[#241D19]">
                            DHARM SAROVAR
                        </span>
                        <span className="block text-[9px] uppercase tracking-[0.32em] text-[#C85A17] font-medium">
                            Private Limited
                        </span>
                    </span>
                </a>
                <nav className="hidden md:flex items-center gap-7">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="text-sm text-[#6E6259] hover:text-[#C85A17] transition-colors duration-300"
                        >
                            {t(l.label)}
                        </a>
                    ))}
                    <button
                        data-testid="lang-toggle-button"
                        onClick={() => setLang(lang === "en" ? "hi" : "en")}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[#E6DBCF] text-sm text-[#241D19] hover:bg-[#F4EDE2] transition-colors duration-300"
                    >
                        <Languages size={15} className="text-[#C85A17]" />
                        {langLabel}
                    </button>
                    <a
                        href="#book"
                        data-testid="nav-book-now-button"
                        className="px-5 py-2 rounded-full bg-[#C85A17] text-white text-sm font-medium hover:bg-[#9C370B] transition-colors duration-300"
                    >
                        {t("Book Now")}
                    </a>
                </nav>
                <div className="md:hidden flex items-center gap-2">
                    <button
                        data-testid="lang-toggle-mobile-button"
                        onClick={() => setLang(lang === "en" ? "hi" : "en")}
                        className="px-3 py-1.5 rounded-full border border-[#E6DBCF] text-xs text-[#241D19]"
                    >
                        {lang === "en" ? "हिंदी" : "EN"}
                    </button>
                    <button
                        data-testid="nav-mobile-menu-button"
                        className="text-[#241D19]"
                        onClick={() => setOpen(!open)}
                        aria-label="Menu"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="md:hidden border-t border-[#E6DBCF] bg-[#FAF6F0] px-6 py-5 flex flex-col gap-5" data-testid="nav-mobile-menu">
                    {links.map((l, i) => (
                        <a
                            key={l.href}
                            href={l.href}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                            className={`text-lg ${i === 0 ? "text-[#C85A17] font-medium" : "text-[#241D19]"}`}
                        >
                            {t(l.label)}
                        </a>
                    ))}
                    <a
                        href="#book"
                        onClick={() => setOpen(false)}
                        data-testid="nav-mobile-book-button"
                        className="px-5 py-3 rounded-full bg-[#C85A17] text-white text-sm font-medium text-center"
                    >
                        {t("Book Now")}
                    </a>
                    <a
                        href="/admin"
                        data-testid="nav-mobile-admin-link"
                        className="text-xs uppercase tracking-[0.3em] text-[#6E6259] pt-2 border-t border-[#E6DBCF]"
                    >
                        Admin
                    </a>
                </div>
            )}
        </header>
    );
};
